import React, { Component } from 'react'
import enhanceWithClickOutside from 'react-click-outside'
import classNames from 'classnames'
import { Arrow } from './Arrow'
import { ActionItem } from './ActionItem'
import { ChartFormat } from './ChartFormat'
import { ColorScheme } from './ColorScheme'
import bem from 'src/utils/bem'
import styles from './ExportMenu.module.scss'
import { intl } from 'src/i18n'

interface ExportHandler {
  text: string
  onclick(options: any): void
}

interface YAxisConfig {
  min: number
  max: number
}

interface Props {
  handlers: ExportHandler[]
  from: string
  to: string
  isMobile: boolean
  yAxisConf: YAxisConfig[]
  isFullScreen: boolean
  generateExportConfig: (
    colorScheme: ColorScheme,
    chartFormat: ChartFormat,
    from: string,
    to: string,
    yAxisConf: any,
    choosenFrame: string
  ) => any
  isAlwaysInverted?: boolean
  choosenFrame: string
}

interface State {
  isOpen: boolean
  colorScheme: ColorScheme | null
  chartFormat: ChartFormat
}

const cName = bem.with('ExportMenu')

class ExportMenuComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isOpen: false,
      colorScheme: null,
      chartFormat: ChartFormat.FULL,
    }
  }

  toggleMenu = () => {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
      colorScheme: null,
      chartFormat: ChartFormat.FULL,
    }))
  }

  toggleColorScheme = (scheme: ColorScheme) => {
    this.setState({
      colorScheme: scheme,
    })
  }

  setChartFormat = (format: ChartFormat) => {
    this.setState({
      chartFormat: format,
    })
  }

  handleClickOutside = () => {
    this.setState({
      isOpen: false,
      colorScheme: null,
    })
  }

  makeSize = (format: ChartFormat) => {
    switch (format) {
      case ChartFormat.FULL:
        return {
          width: 1200,
          height: 900,
        }
      case ChartFormat.HORIZONTAL:
        return {
          width: 1200,
          height: 450,
        }
      case ChartFormat.VERTICAL:
        return {
          width: 450,
          height: 900,
        }
      default:
        throw Error(`Unsupported chart format ${format}`)
    }
  }

  isActiveColor(scheme: ColorScheme) {
    const { colorScheme } = this.state
    return scheme === colorScheme
  }

  schemeSelectHandler = (e: any, scheme: ColorScheme) => {
    e.stopPropagation()
    this.toggleColorScheme(scheme)
  }

  render() {
    const {
      handlers,
      isMobile,
      isFullScreen,
      from,
      to,
      yAxisConf,
      isAlwaysInverted,
      choosenFrame,
    } = this.props
    const { isOpen, colorScheme, chartFormat } = this.state
    const inverted = isMobile || isFullScreen || isAlwaysInverted

    return (
      <div className={styles.root}>
        <div className={cName('button')} onClick={this.toggleMenu}>
          {isOpen && (
            <ul
              className={classNames(
                cName('themes', { inverted, isAlwaysInverted })
              )}
            >
              <li
                className={classNames(
                  cName('theme', {
                    active: this.isActiveColor(ColorScheme.DARK),
                    inverted,
                  })
                )}
                onMouseEnter={e =>
                  this.schemeSelectHandler(e, ColorScheme.DARK)
                }
                onClick={e => this.schemeSelectHandler(e, ColorScheme.DARK)}
              >
                <span className={classNames(cName('title', { inverted }))}>
                  {intl.t(
                    'components.timeLineChart.components.exportMenu.dark'
                  )}
                </span>
                <Arrow classNames={classNames(cName('arrow', { inverted }))} />
              </li>
              <li
                //eslint-disable-next-line
                className={classNames(cName('theme', { active: this.isActiveColor(ColorScheme.BRIGHT), inverted }))}
                onMouseEnter={e =>
                  this.schemeSelectHandler(e, ColorScheme.BRIGHT)
                }
                onClick={e => this.schemeSelectHandler(e, ColorScheme.BRIGHT)}
              >
                <span className={classNames(cName('title', { inverted }))}>
                  {intl.t(
                    'components.timeLineChart.components.exportMenu.bright'
                  )}
                </span>
                <Arrow classNames={classNames(cName('arrow', { inverted }))} />
              </li>
            </ul>
          )}
          {colorScheme && (
            <ul
              className={classNames(
                cName('format-options', { inverted, isAlwaysInverted })
              )}
            >
              {handlers.map(({ text, onclick }) => (
                <ActionItem
                  key={text.replace(/\s/g, '-')}
                  text={text}
                  onClick={() =>
                    onclick(
                      this.props.generateExportConfig(
                        colorScheme,
                        chartFormat,
                        from,
                        to,
                        yAxisConf,
                        choosenFrame
                      )
                    )
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    )
  }
}

export const ExportMenu = enhanceWithClickOutside(ExportMenuComponent)
